import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { button } from '@vitesse/base-ui/components/base-buttons';
import type { CameraManager } from '../../managers/camera-manager';
import type { CameraStreamManager } from './camera-stream-manager';
import type { Camera } from '../../../core/domain/camera';
import './camera-playback';
import '@vitesse/base-ui/elements/vitesse-icon';
// fmtBytes is DECIMAL and shared. This file had its own binary copy printing "GB" for 2³⁰ bytes,
// which is the third definition of that word the product had shipped — and the one attached to a
// disk-usage figure a customer compares against a plan quoted in decimal GB.
import { fmtDateTime, fmtBytes } from '@vitesse/base-ui/format';

/*
 * <vitesse-camera-recordings> — the Recording tab body: the continuous DVR player
 * (HLS, whole-timeline seek + live tail) on top, and a compact recording control
 * panel below — an LED storage gauge (used / cap) + the rolling-vs-stop buffer
 * toggle. Recording state + stats ride the camera descriptor (the roster
 * subscription), so the panel reads them straight off `camera`. The enable/pause/
 * download controls live in the page header (renderActions), not here.
 */

const SEGMENTS = 28;

const STYLE_ID = 'vitesse-camera-recordings-styles';
function ensureStyles(): void {
  // Theme-correct: every colour is a canonical token from tokens.css that flips
  // light/dark ([data-theme]) — NO hardcoded dark fallbacks. The earlier bug used
  // non-existent --surface/--surface-sunken, so it ALWAYS hit the dark fallback,
  // while --text-primary correctly flipped dark in light mode → dark text on a
  // dark panel = the "inversed" look. Real bg tokens fix it in both themes.
  const css = `
vitesse-camera-recordings { display: block; height: 100%; }
.cam-rec2 { display: grid; grid-template-rows: minmax(0, 1fr) auto; gap: 14px; height: 100%; }
.cam-rec2-panel {
  border: 1px solid var(--border-color); border-radius: var(--radius-md, 8px);
  padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; background: var(--bg-secondary);
}
.cam-rec2-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; font-size: 13px; }
.cam-rec2-label { color: var(--text-secondary); font-weight: 600; }
.cam-rec2-value { color: var(--text-primary); font-variant-numeric: tabular-nums; }
/* LED storage gauge — fuel-gauge ZONES by position (green → amber → red), so the
   colour of a lit segment shows how full that point is and the saturated end goes
   red. Unlit track uses --bg-tertiary so it reads on both themes. */
.cam-rec2-led { display: flex; gap: 3px; height: 14px; }
.cam-rec2-seg { flex: 1; border-radius: 2px; background: var(--bg-tertiary);
  box-shadow: inset 0 0 0 1px var(--border-color); transition: background 0.2s, box-shadow 0.2s; }
.cam-rec2-seg.on.z-green { background: var(--green); box-shadow: 0 0 4px color-mix(in srgb, var(--green) 55%, transparent); }
.cam-rec2-seg.on.z-amber { background: var(--amber); box-shadow: 0 0 4px color-mix(in srgb, var(--amber) 55%, transparent); }
.cam-rec2-seg.on.z-red { background: var(--red); box-shadow: 0 0 5px color-mix(in srgb, var(--red) 70%, transparent); }`;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

function fmtTime(ms: number): string {
  return ms > 0 ? fmtDateTime(ms) : '—';
}

@customElement('vitesse-camera-recordings')
export class CameraRecordings extends LitElement {
  @property({ attribute: false }) cameraManager?: CameraManager;
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  @property({ attribute: false }) camera?: Camera;
  @property() gatewayId = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }
  connectedCallback(): void {
    super.connectedCallback();
    ensureStyles();
  }

  private async toggleCircular(): Promise<void> {
    const c = this.camera;
    if (!c || !this.cameraManager) return;
    try {
      await this.cameraManager.setRecordingCircular(c.id, !c.recordingCircular);
    } catch (err) {
      console.error('[camera] setRecordingCircular failed', err);
    }
  }

  render() {
    const c = this.camera;
    if (!c) return nothing;
    const used = c.bytesUsed;
    const max = c.bytesMax || 1;
    const frac = Math.max(0, Math.min(1, used / max));
    const lit = Math.round(frac * SEGMENTS);
    // Fuel-gauge zones by POSITION (not one colour for the whole bar): the first
    // ~45% of the bar is green, the middle amber, the saturated end red — so a lit
    // segment's colour tells you HOW FULL that point is. As usage climbs, the lit
    // run reaches into the red end = "approaching saturation".
    const zoneOf = (i: number): string => {
      const p = i / SEGMENTS;
      return p < 0.45 ? 'z-green' : p < 0.78 ? 'z-amber' : 'z-red';
    };
    const hasFootage = c.availableFromMs > 0;
    return html`<div class="cam-rec2">
      <vitesse-camera-playback
        .cameraManager=${this.cameraManager}
        .cameraStreams=${this.cameraStreams}
        .camera=${c}
        .cameraId=${c.id}
        .gatewayId=${this.gatewayId}
        ?hasFootage=${hasFootage}
      ></vitesse-camera-playback>
      <div class="cam-rec2-panel">
        <div class="cam-rec2-row">
          <span class="cam-rec2-label">${t('camera.storage')}</span>
          <span class="cam-rec2-value">${fmtBytes(used)} / ${fmtBytes(max)}</span>
        </div>
        <div class="cam-rec2-led">
          ${Array.from({ length: SEGMENTS }, (_, i) => html`<span class="cam-rec2-seg ${i < lit ? `on ${zoneOf(i)}` : ''}"></span>`)}
        </div>
        <div class="cam-rec2-row">
          <span class="cam-rec2-label">${t('camera.recorded')}</span>
          <span class="cam-rec2-value">${hasFootage ? `${fmtTime(c.availableFromMs)} → ${fmtTime(c.availableToMs)}` : '—'}</span>
        </div>
        <div class="cam-rec2-row">
          <span class="cam-rec2-label">${t('camera.bufferMode')}</span>
          ${button({
            label: c.recordingCircular ? t('camera.bufferRolling') : t('camera.bufferStop'),
            icon: c.recordingCircular ? 'refresh-cw' : 'square',
            color: c.recordingCircular ? 'blue' : 'yellow',
            size: 'sm',
            onClick: () => void this.toggleCircular(),
          })}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-camera-recordings': CameraRecordings;
  }
}
